"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.InlineSelect = exports.InlineSelectElement = void 0;
// InlineSelectInput.ts
const web_components_1 = require("../web-components");
const InlineSelectStyles_1 = require("./InlineSelectStyles");
class InlineSelectElement extends HTMLElement {
    triggerEl;
    hiddenInputEl;
    listEl;
    // private menuEl!: HTMLDivElement;
    // private valueEl!: HTMLSpanElement;
    internalId = `inline-select-${Math.random().toString(16).slice(2)}`;
    isOpen = false;
    activeValue = null;
    static get observedAttributes() {
        return ["label", "value", "name", "disabled", "placeholder", "options"];
    }
    connectedCallback() {
        const idAttr = this.getAttribute("id");
        if (idAttr)
            this.internalId = idAttr;
        document.addEventListener("mousedown", this.onDocMouseDown, true);
        this.render();
    }
    disconnectedCallback() {
        document.removeEventListener("mousedown", this.onDocMouseDown, true);
    }
    attributeChangedCallback() {
        this.render();
    }
    // public API -------------------------------------------------------------
    get value() {
        return this.getAttribute("value") ?? "";
    }
    set value(v) {
        if (v === null || v === undefined || v === "")
            this.removeAttribute("value");
        else
            this.setAttribute("value", v);
    }
    setOptions(options) {
        this.setAttribute("options", JSON.stringify(options));
    }
    // helpers ----------------------------------------------------------------
    parseOptions() {
        const raw = this.getAttribute("options") || "[]";
        try {
            const arr = JSON.parse(raw);
            if (!Array.isArray(arr))
                return [];
            return arr
                .map((o) => ({
                value: String(o?.value ?? ""),
                label: String(o?.label ?? ""),
                shortLabel: o?.shortLabel
                    ? String(o.shortLabel)
                    : undefined,
                disabled: !!o?.disabled,
            }))
                .filter((o) => o.value !== "" && o.label !== "");
        }
        catch {
            console.warn("[InlineSelectInput] Invalid JSON in `options`");
            return [];
        }
    }
    getSelectedOption(options, value) {
        return options.find((o) => o.value === value);
    }
    getTriggerText(options, value) {
        const placeholder = this.getAttribute("placeholder") ?? "";
        const selected = this.getSelectedOption(options, value);
        const isPlaceholder = !selected;
        const displayText = selected
            ? selected.shortLabel || selected.label
            : placeholder;
        return { displayText, isPlaceholder };
    }
    setValueAndEmit(nextValue) {
        this.setAttribute("value", nextValue);
        if (this.hiddenInputEl)
            this.hiddenInputEl.value = nextValue;
        // IMPORTANT: emit only when selecting an option
        this.dispatchEvent(new Event("change", { bubbles: true }));
    }
    openMenu() {
        if (this.hasAttribute("disabled"))
            return;
        this.isOpen = true;
        const options = this.parseOptions();
        const current = this.value;
        const firstEnabled = options.find((option) => !option.disabled)?.value ?? null;
        this.activeValue = current || firstEnabled;
        this.render();
        requestAnimationFrame(() => {
            // keep focus on trigger for keyboard nav
            this.triggerEl?.focus();
            // ensure active item is visible
            if (this.activeValue) {
                const el = this.querySelector(`[data-option-value="${CSS.escape(this.activeValue)}"]`);
                el?.scrollIntoView({ block: "nearest" });
            }
        });
    }
    closeMenu() {
        this.isOpen = false;
        this.activeValue = null;
        this.render();
        requestAnimationFrame(() => this.triggerEl?.focus());
    }
    toggleMenu() {
        if (this.isOpen)
            this.closeMenu();
        else
            this.openMenu();
    }
    onDocMouseDown = (e) => {
        if (!this.isOpen)
            return;
        const path = e.composedPath?.() ?? [];
        if (!path.includes(this))
            this.closeMenu();
    };
    onTriggerClick = (e) => {
        e.preventDefault();
        if (this.hasAttribute("disabled"))
            return;
        this.toggleMenu();
    };
    moveActive(delta) {
        const options = this.parseOptions().filter((o) => !o.disabled);
        if (options.length === 0)
            return;
        const index = Math.max(0, options.findIndex((o) => o.value === this.activeValue));
        let next = index + delta;
        if (next < 0)
            next = options.length - 1;
        if (next >= options.length)
            next = 0;
        this.activeValue = options[next].value;
        // update only list state (cheap render is fine here too)
        this.render();
        requestAnimationFrame(() => {
            const el = this.querySelector(`[data-option-value="${CSS.escape(this.activeValue ?? "")}"]`);
            el?.scrollIntoView({ block: "nearest" });
            this.triggerEl?.focus();
        });
    }
    onKeyDown = (e) => {
        if (this.hasAttribute("disabled"))
            return;
        // closed state shortcuts
        if (!this.isOpen) {
            if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
                e.preventDefault();
                this.openMenu();
            }
            return;
        }
        // open state navigation
        if (e.key === "Escape") {
            e.preventDefault();
            this.closeMenu();
            return;
        }
        if (e.key === "ArrowDown") {
            e.preventDefault();
            this.moveActive(1);
            return;
        }
        if (e.key === "ArrowUp") {
            e.preventDefault();
            this.moveActive(-1);
            return;
        }
        if (e.key === "Enter") {
            e.preventDefault();
            if (!this.activeValue)
                return;
            const options = this.parseOptions();
            const active = options.find((o) => o.value === this.activeValue);
            if (!active || active.disabled)
                return;
            this.setValueAndEmit(this.activeValue);
            this.closeMenu();
            return;
        }
        if (e.key === "Tab")
            this.closeMenu();
    };
    onOptionClick = (e) => {
        const target = e.target;
        const optionEl = target.closest("[data-option-value]");
        if (!optionEl)
            return;
        const value = optionEl.getAttribute("data-option-value") ?? "";
        const disabled = optionEl.getAttribute("data-disabled") === "true";
        if (!value || disabled)
            return;
        this.setValueAndEmit(value);
        this.closeMenu();
    };
    render() {
        console.log("render(): [InlineSelect] Render called ---------------");
        const id = this.internalId;
        const label = this.getAttribute("label") ?? "Label";
        const name = this.getAttribute("name") ?? "";
        const value = this.getAttribute("value") ?? "";
        const disabled = this.hasAttribute("disabled");
        const options = this.parseOptions();
        const { displayText, isPlaceholder } = this.getTriggerText(options, value);
        // build menu list (white card style)
        const listMarkup = options.length === 0
            ? `<div class="InlineSelect__empty" role="presentation">No options</div>`
            : options
                .map((o, idx) => {
                const isSelected = value === o.value;
                const isActive = this.activeValue === o.value;
                const rowClass = [
                    "InlineSelect__option",
                    isSelected ? "is-selected" : "",
                    isActive ? "is-active" : "",
                    o.disabled ? "is-disabled" : "",
                ]
                    .filter(Boolean)
                    .join(" ");
                const divider = idx === 0
                    ? ""
                    : `<div class="InlineSelect__divider" role="presentation"></div>`;
                return `
                            ${divider}
                            <div class="${rowClass}"
                                role="option"
                                aria-selected="${isSelected ? "true" : "false"}"
                                data-option-value="${(0, web_components_1.escapeHTMLChars)(o.value)}"
                                data-disabled="${o.disabled ? "true" : "false"}"
                                title="${(0, web_components_1.escapeHTMLChars)(o.label)}"
                                >
                                <span class="InlineSelect__optionText ${isSelected ? "is-selectedText" : ""}">
                                    ${(0, web_components_1.escapeHTMLChars)(o.label)}
                                </span>
                                <span class="InlineSelect__optionRight" aria-hidden="true">
                                    ${isSelected
                    ? `<dc-icon class="InlineSelect__check" family="regular" name="check" size="md"></dc-icon>`
                    : ""}
                                </span>
                            </div>
                        `;
            })
                .join("");
        this.innerHTML = `
            <style>
                inline-select { ${InlineSelectStyles_1.styles.host} }
                inline-select .InlineSelectWrap { ${InlineSelectStyles_1.styles.wrap} }

                inline-select .InlineSelect { ${InlineSelectStyles_1.styles.inlineSelect} }
                inline-select .InlineSelect:focus-visible { ${InlineSelectStyles_1.styles.inlineSelectFocus} }
                inline-select .InlineSelect__label { ${InlineSelectStyles_1.styles.inlineLabel} }
                inline-select .InlineSelect__value { ${InlineSelectStyles_1.styles.inlineValue} }
                inline-select .InlineSelect__placeholder { ${InlineSelectStyles_1.styles.inlinePlaceholder} }
                inline-select .InlineSelect__icon { ${InlineSelectStyles_1.styles.inlineIcon} }
                inline-select .InlineSelect.is-disabled { ${InlineSelectStyles_1.styles.disabled} }

                /* NEW: dropdown menu styles (design on the right) */
                inline-select .InlineSelect__menu { ${InlineSelectStyles_1.styles.menu} }
                inline-select .InlineSelect__menu[hidden] { display: none; }
                inline-select .InlineSelect__list { ${InlineSelectStyles_1.styles.list} }
                inline-select .InlineSelect__divider { ${InlineSelectStyles_1.styles.divider} }
                inline-select .InlineSelect__option { ${InlineSelectStyles_1.styles.option} }
                inline-select .InlineSelect__option.is-active { ${InlineSelectStyles_1.styles.optionActive} }
                inline-select .InlineSelect__option.is-disabled { ${InlineSelectStyles_1.styles.optionDisabled} }
                inline-select .InlineSelect__optionText { ${InlineSelectStyles_1.styles.optionText} }
                inline-select .InlineSelect__optionText.is-selectedText { ${InlineSelectStyles_1.styles.optionTextSelected} }
                inline-select .InlineSelect__optionRight { ${InlineSelectStyles_1.styles.optionRight} }
                inline-select .InlineSelect__check { ${InlineSelectStyles_1.styles.check} }
                inline-select .InlineSelect__empty { ${InlineSelectStyles_1.styles.empty} }
            </style>

            <div class="InlineSelectWrap">
                <div
                    id="${(0, web_components_1.escapeHTMLChars)(id)}"
                    class="InlineSelect ${disabled ? "is-disabled" : ""}"
                    role="combobox"
                    aria-expanded="${this.isOpen ? "true" : "false"}"
                    aria-disabled="${disabled ? "true" : "false"}"
                    aria-haspopup="listbox"
                    aria-controls="menu-${(0, web_components_1.escapeHTMLChars)(id)}"
                    tabindex="${disabled ? "-1" : "0"}"
                >
                    <span class="InlineSelect__label body-M">${(0, web_components_1.escapeHTMLChars)(label)}</span>
                    <span class="InlineSelect__value body-M ${isPlaceholder ? "InlineSelect__placeholder" : ""}">
                        ${(0, web_components_1.escapeHTMLChars)(displayText)}
                    </span>
                    <span class="InlineSelect__icon" aria-hidden="true">
                        <dc-icon family="regular" name="chevron-down" size="sm"></dc-icon>
                    </span>
                </div>

                <input type="hidden" class="InlineSelect__hidden"
                    ${name ? `name="${(0, web_components_1.escapeHTMLChars)(name)}"` : ""}
                    value="${(0, web_components_1.escapeHTMLChars)(value)}"
                />

                <div id="menu-${(0, web_components_1.escapeHTMLChars)(id)}" class="InlineSelect__menu" role="presentation" ${this.isOpen ? "" : "hidden"}>
                <div class="InlineSelect__list" role="listbox">
                    ${listMarkup}
                </div>
                </div>
            </div>
        `;
        // wire elements
        this.triggerEl = this.querySelector(".InlineSelect");
        this.hiddenInputEl = this.querySelector(".InlineSelect__hidden");
        this.listEl = this.querySelector(".InlineSelect__list");
        // listeners (rebind each render)
        this.triggerEl.onclick = null;
        this.triggerEl.onkeydown = null;
        this.listEl.onclick = null;
        this.triggerEl.addEventListener("click", this.onTriggerClick);
        this.triggerEl.addEventListener("keydown", this.onKeyDown);
        this.listEl.addEventListener("click", this.onOptionClick);
        // keep hidden input synced
        if (this.hiddenInputEl)
            this.hiddenInputEl.value = value;
        // if menu open, ensure activeValue is set
        if (this.isOpen && !this.activeValue) {
            const firstEnabled = options.find((o) => !o.disabled)?.value ?? null;
            this.activeValue = value || firstEnabled;
        }
    }
}
exports.InlineSelectElement = InlineSelectElement;
const InlineSelect = (props) => {
    const { label, name, value, placeholder, disabled, options } = props.attributes;
    const id = props.attributes.id || `inline-select-${Date.now()}`;
    const optionsAttr = options && options.length
        ? `options='${JSON.stringify(options).replace(/'/g, "&apos;")}'`
        : "";
    return `<inline-select
        ${(0, web_components_1.toAttribute)("id", id)}
        ${(0, web_components_1.toAttribute)("label", label ?? "Start week")}
        ${(0, web_components_1.toAttribute)("name", name)}
        ${(0, web_components_1.toAttribute)("value", value)}
        ${(0, web_components_1.toAttribute)("placeholder", placeholder ?? "")}
        ${optionsAttr}
        ${disabled ? "disabled" : ""}>
    </inline-select>`;
};
exports.InlineSelect = InlineSelect;
(0, web_components_1.registerWebComponent)("inline-select", InlineSelectElement);
//# sourceMappingURL=InlineSelect.js.map